import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, Square, Loader2, Sparkles, X, Trash2, Play, Pause } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const formatTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

/**
 * VoiceIncidentRecorder
 * Records audio in the browser, transcribes via Whisper, extracts structured fields via GPT-5.2.
 *
 * Props:
 *  - open (bool)
 *  - onClose ()
 *  - onApply (extracted) — callback with the extracted incident/wildlife fields
 *  - mode ('incident' | 'wildlife') — which extractor + schema to use. Default 'incident'.
 */
export default function VoiceIncidentRecorder({ open, onClose, onApply, mode = 'incident' }) {
  const { t } = useTranslation();
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [language, setLanguage] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [stage, setStage] = useState('idle'); // idle | recording | transcribing | extracting | done
  const [playing, setPlaying] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  // Live captions via Web Speech API (in parallel with MediaRecorder).
  // These are only for on-screen feedback while the user speaks — the
  // final transcript still comes from Whisper (more accurate + multilingual).
  const recognitionRef = useRef(null);
  const [liveCaption, setLiveCaption] = useState('');
  const [liveInterim, setLiveInterim] = useState('');

  const token = localStorage.getItem('token');

  // Cleanup on unmount or close
  useEffect(() => {
    if (!open) {
      reset();
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(tr => tr.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reset = () => {
    setRecording(false);
    setAudioBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setElapsed(0);
    setTranscript('');
    setLanguage(null);
    setExtracted(null);
    setStage('idle');
    setPlaying(false);
    setLiveCaption('');
    setLiveInterim('');
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
      recognitionRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Pick a supported mime
      const mimeOptions = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
      const mime = mimeOptions.find(m => MediaRecorder.isTypeSupported(m)) || '';
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime || 'audio/webm' });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(tr => tr.stop());
          streamRef.current = null;
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      setStage('recording');
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);

      // Live captions (best-effort, browser Web Speech API). Runs in parallel
      // with MediaRecorder — the final transcription still comes from Whisper.
      // Not supported on iOS Safari — silently skip in that case.
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SR) {
        try {
          const rec = new SR();
          // Hint the language when we know it (fallback to English).
          rec.lang = 'en-US';
          rec.interimResults = true;
          rec.continuous = true;
          rec.maxAlternatives = 1;
          rec.onresult = (event) => {
            let finalText = '';
            let interimText = '';
            for (let i = event.resultIndex; i < event.results.length; i += 1) {
              const t = event.results[i][0].transcript;
              if (event.results[i].isFinal) finalText += t;
              else interimText += t;
            }
            if (finalText) setLiveCaption((prev) => (prev ? `${prev} ${finalText.trim()}` : finalText.trim()));
            setLiveInterim(interimText.trim());
          };
          rec.onerror = () => { /* transient errors ignored — caption is optional */ };
          rec.onend = () => { setLiveInterim(''); };
          recognitionRef.current = rec;
          rec.start();
        } catch {
          // Live-captions are non-blocking — swallow if browser refuses
          recognitionRef.current = null;
        }
      }
    } catch (err) {
      console.error(err);
      toast.error('Microphone access denied. Please allow microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
      recognitionRef.current = null;
    }
    setLiveInterim('');
    setRecording(false);
    setStage('idle');
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const togglePlayback = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
  };

  const processAudio = async () => {
    if (!audioBlob) {
      toast.error('No recording available');
      return;
    }
    setStage('transcribing');
    try {
      const fd = new FormData();
      const ext = (audioBlob.type.includes('webm') ? 'webm' : audioBlob.type.includes('ogg') ? 'ogg' : audioBlob.type.includes('mp4') ? 'm4a' : 'webm');
      fd.append('audio', audioBlob, `recording.${ext}`);

      if (mode === 'wildlife' || mode === 'fatigue') {
        // Two-step: transcribe then extract-{mode}
        const tRes = await fetch(`${API_BASE}/api/voice-incident/transcribe`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!tRes.ok) {
          const errBody = await tRes.json().catch(() => ({}));
          throw new Error(errBody.detail || `Transcription failed (${tRes.status})`);
        }
        const tData = await tRes.json();
        const tx = (tData.transcript || '').trim();
        setTranscript(tx);
        setLanguage(tData.language || null);
        if (!tx) {
          setStage('done');
          toast.warning('Transcription empty. Try recording again.');
          return;
        }
        setStage('extracting');
        const xRes = await fetch(`${API_BASE}/api/voice-incident/extract-${mode}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ transcript: tx, transcript_language: tData.language || null }),
        });
        if (!xRes.ok) {
          const errBody = await xRes.json().catch(() => ({}));
          throw new Error(errBody.detail || `${mode} extraction failed (${xRes.status})`);
        }
        const xData = await xRes.json();
        setExtracted(xData);
        setStage('done');
        toast.success('Voice processed — review and apply.');
        return;
      }

      // Default: incident /process (one call)
      const res = await fetch(`${API_BASE}/api/voice-incident/process`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || `Processing failed (${res.status})`);
      }
      const data = await res.json();
      setTranscript(data.transcript || '');
      setLanguage(data.language || null);
      setExtracted(data.extracted || null);
      setStage('done');
      if (data.extracted) {
        toast.success('Voice processed — review and apply.');
      } else {
        toast.warning('Transcription empty. Try recording again.');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Voice processing failed');
      setStage('idle');
    }
  };

  const reExtract = async () => {
    if (!transcript.trim()) {
      toast.error('No transcript to process');
      return;
    }
    setStage('extracting');
    const endpoint = (mode === 'wildlife' || mode === 'fatigue')
      ? `${API_BASE}/api/voice-incident/extract-${mode}`
      : `${API_BASE}/api/voice-incident/extract`;
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript, transcript_language: language }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || `Extraction failed (${res.status})`);
      }
      const data = await res.json();
      setExtracted(data);
      setStage('done');
      toast.success('Re-extracted from edited transcript');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Extraction failed');
      setStage('done');
    }
  };

  const apply = () => {
    if (!extracted) return;
    onApply(extracted);
    toast.success('Voice incident applied to form');
    onClose();
  };

  const isBusy = stage === 'transcribing' || stage === 'extracting';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="bg-slate-950 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="voice-recorder-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Mic className="w-5 h-5 text-sky-400" />
            {mode === 'wildlife'
              ? t('voiceIncident.wildlifeTitle', 'Voice-to-Wildlife-Strike')
              : mode === 'fatigue'
                ? t('voiceIncident.fatigueTitle', 'Voice-to-Fatigue-Report')
                : t('voiceIncident.title', 'Voice-to-Incident')}
            <Badge className="bg-violet-600/20 text-violet-300 border border-violet-600/40 ml-2">
              <Sparkles className="w-3 h-3 mr-1" /> Whisper + GPT-5.2
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {mode === 'wildlife'
              ? t('voiceIncident.wildlifeSubtitle', 'Describe the bird / wildlife strike in English or Arabic. AI transcribes and extracts species, damage, phase of flight and more.')
              : mode === 'fatigue'
                ? t('voiceIncident.fatigueSubtitle', 'Speak your fatigue report in English or Arabic. AI transcribes and extracts hours awake, duty time, contributing factors, and safety impact.')
                : t('voiceIncident.subtitle', 'Speak the incident in English or Arabic; AI transcribes and extracts structured fields.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Recording control */}
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-5 flex flex-col items-center gap-4">
              {!recording && !audioBlob && (
                <Button
                  onClick={startRecording}
                  className="h-20 w-20 rounded-full bg-rose-600 hover:bg-rose-500 shadow-lg shadow-rose-900/40"
                  data-testid="voice-record-start"
                >
                  <Mic className="w-8 h-8" />
                </Button>
              )}
              {recording && (
                <>
                  <div className="flex items-center gap-3">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75" />
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-rose-500" />
                    </span>
                    <span className="text-rose-300 font-mono text-lg tabular-nums">{formatTime(elapsed)}</span>
                  </div>
                  <Button
                    onClick={stopRecording}
                    className="h-16 w-16 rounded-full bg-slate-700 hover:bg-slate-600"
                    data-testid="voice-record-stop"
                  >
                    <Square className="w-6 h-6" />
                  </Button>
                  {/* Live captions — Web Speech API (browser-native) */}
                  {(liveCaption || liveInterim) && (
                    <div
                      className="w-full rounded-md bg-slate-950/80 border border-slate-800 p-3 mt-1 max-h-40 overflow-y-auto"
                      data-testid="voice-live-captions"
                    >
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        Live captions (draft)
                      </div>
                      <p className="text-sm text-slate-200 leading-relaxed">
                        <span>{liveCaption}</span>
                        {liveInterim && (
                          <span className="text-slate-500 italic">
                            {liveCaption ? ' ' : ''}{liveInterim}
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </>
              )}
              {!recording && audioBlob && (
                <div className="w-full space-y-3">
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      onClick={togglePlayback}
                      variant="outline"
                      size="icon"
                      className="border-slate-700 text-slate-200"
                      data-testid="voice-play-toggle"
                    >
                      {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </Button>
                    <audio
                      ref={audioRef}
                      src={audioUrl}
                      onPlay={() => setPlaying(true)}
                      onPause={() => setPlaying(false)}
                      onEnded={() => setPlaying(false)}
                      className="flex-1"
                      controls
                    />
                    <Button
                      type="button"
                      onClick={reset}
                      variant="ghost"
                      size="icon"
                      className="text-slate-400 hover:text-rose-400"
                      data-testid="voice-discard"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  {stage !== 'done' && (
                    <Button
                      onClick={processAudio}
                      disabled={isBusy}
                      className="w-full bg-sky-600 hover:bg-sky-500"
                      data-testid="voice-process-btn"
                    >
                      {isBusy ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('voiceIncident.processing', 'Transcribing & extracting...')}</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-2" /> {t('voiceIncident.process', 'Transcribe & Extract')}</>
                      )}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Transcript */}
          {transcript && (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-200">{t('voiceIncident.transcript', 'Transcript')}</h3>
                  {language && (
                    <Badge variant="outline" className="border-slate-700 text-slate-300 uppercase">
                      {language}
                    </Badge>
                  )}
                </div>
                <Textarea
                  value={transcript}
                  onChange={(e) => setTranscript(e.target.value)}
                  rows={4}
                  className="bg-slate-950 border-slate-800 text-slate-100"
                  data-testid="voice-transcript"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={reExtract}
                  disabled={isBusy}
                  className="border-slate-700 text-slate-200"
                  data-testid="voice-reextract-btn"
                >
                  {stage === 'extracting' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {t('voiceIncident.reExtract', 'Re-extract from edited transcript')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Extracted preview */}
          {extracted && (
            <Card className="bg-gradient-to-br from-violet-950/40 to-slate-900/60 border-violet-800/40">
              <CardContent className="p-5 space-y-3" data-testid="voice-extracted-preview">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-violet-200 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> {t('voiceIncident.extracted', 'Extracted fields')}
                  </h3>
                  {extracted.confidence != null && (
                    <Badge className="bg-violet-700/40 text-violet-200 border border-violet-600/40">
                      {Math.round(extracted.confidence * 100)}% confidence
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <Field label="Title" value={extracted.title} />
                  {mode === 'wildlife' ? (
                    <>
                      <Field label="Species" value={extracted.species_name} />
                      <Field label="Category" value={extracted.wildlife_category} />
                      <Field label="Size" value={extracted.size} />
                      <Field label="Number Struck" value={extracted.number_struck} />
                      <Field label="Phase of Flight" value={extracted.phase_of_flight} />
                      <Field label="Damage Level" value={extracted.damage_level} />
                      <Field label="Effect on Flight" value={extracted.effect_on_flight} />
                      <Field label="Airport (ICAO)" value={extracted.airport_icao} />
                      <Field label="Runway" value={extracted.runway} />
                      <Field label="Altitude (ft)" value={extracted.altitude_ft} />
                      <Field label="Date" value={extracted.occurrence_date} />
                      <Field label="Time" value={extracted.occurrence_time} />
                      <Field label="Aircraft Reg" value={extracted.aircraft_registration} />
                      <Field label="Aircraft Type" value={extracted.aircraft_type} />
                      <Field label="Flight #" value={extracted.flight_number} />
                    </>
                  ) : mode === 'fatigue' ? (
                    <>
                      <Field label="Report Type" value={extracted.report_type} />
                      <Field label="Event Date" value={extracted.event_date} />
                      <Field label="Hours Awake" value={extracted.hours_awake} />
                      <Field label="Hours on Duty" value={extracted.hours_on_duty} />
                      <Field label="Sleep last 24h" value={extracted.sleep_in_last_24h} />
                      <Field label="Safety Impact" value={extracted.safety_impact} />
                      <Field label="Flight #" value={extracted.flight_number} />
                      <Field label="Aircraft Reg" value={extracted.aircraft_registration} />
                      <Field label="Anonymous" value={extracted.anonymous === true ? 'yes' : extracted.anonymous === false ? 'no' : null} />
                    </>
                  ) : (
                    <>
                      <Field label="Category" value={extracted.occurrence_category} />
                      <Field label="Severity" value={extracted.severity} />
                      <Field label="Likelihood" value={extracted.likelihood} />
                      <Field label="Date" value={extracted.occurrence_date} />
                      <Field label="Location" value={extracted.location} />
                      <Field label="Flight #" value={extracted.flight_number} />
                      <Field label="Aircraft Reg" value={extracted.aircraft_registration} />
                      <Field label="Aircraft Type" value={extracted.aircraft_type} />
                    </>
                  )}
                </div>
                <Field label="Summary" value={extracted.summary} multiline />
                <Field label="Description" value={extracted.description} multiline />
                {mode === 'wildlife' && extracted.parts_struck && extracted.parts_struck.length > 0 && (
                  <div>
                    <span className="text-xs uppercase tracking-wider text-slate-500">Parts struck</span>
                    <ul className="list-disc list-inside text-slate-300 mt-1">
                      {extracted.parts_struck.map((p, i) => (
                        <li key={i} className="text-sm">{p}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {mode === 'fatigue' && extracted.factors && extracted.factors.length > 0 && (
                  <div>
                    <span className="text-xs uppercase tracking-wider text-slate-500">Contributing factors</span>
                    <ul className="list-disc list-inside text-slate-300 mt-1">
                      {extracted.factors.map((f, i) => (
                        <li key={i} className="text-sm">{f}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {mode !== 'wildlife' && mode !== 'fatigue' && extracted.contributing_factors && extracted.contributing_factors.length > 0 && (
                  <div>
                    <span className="text-xs uppercase tracking-wider text-slate-500">Contributing factors</span>
                    <ul className="list-disc list-inside text-slate-300 mt-1">
                      {extracted.contributing_factors.map((f, i) => (
                        <li key={i} className="text-sm">{f}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-slate-700 text-slate-200"
              data-testid="voice-cancel-btn"
            >
              <X className="w-4 h-4 mr-2" /> {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="button"
              onClick={apply}
              disabled={!extracted || isBusy}
              className="bg-emerald-600 hover:bg-emerald-500"
              data-testid="voice-apply-btn"
            >
              {t('voiceIncident.apply', 'Apply to Form')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, value, multiline = false }) => (
  <div className={multiline ? 'col-span-full' : ''}>
    <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
    <p className={`text-slate-100 mt-0.5 ${multiline ? 'whitespace-pre-wrap' : 'truncate'}`}>
      {value || <span className="text-slate-600 italic">—</span>}
    </p>
  </div>
);
